import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Form,
  Select,
  Input,
  Button,
  Row,
  Col,
  Typography,
  message,
  Icon,
  Table,
} from "antd";
import { platformApi } from "../../../../../api";
import debounce from "lodash/debounce";

const { Title } = Typography;
const { Option } = Select;
const { Item } = Form;

const extractActiveBins = (spareItem) => {
  if (!spareItem) return [];
  let binList = [];
  if (typeof spareItem.binLocations === "string") {
    try {
      binList = JSON.parse(spareItem.binLocations);
    } catch (e) {
      binList = [];
    }
  } else if (Array.isArray(spareItem.binLocations)) {
    binList = spareItem.binLocations;
  }

  let activeBins = binList.filter((b) => (b.phyQuantity || 0) > 0);

  // If no bins with phyQuantity > 0 found in JSON, check top-level binNum/binLocation
  if (activeBins.length === 0 && (spareItem.binNum || spareItem.binLocation)) {
    const bName = spareItem.binNum || spareItem.binLocation;
    activeBins = [
      {
        binNum: bName,
        binLocation: bName,
        phyQuantity: spareItem.phyQuantity || 0,
      },
    ];
  }

  // Fallback: If still empty but phyQuantity > 0 (or row has stock), create a default bin entry
  if (activeBins.length === 0 && (spareItem.phyQuantity || 0) > 0) {
    activeBins = [
      {
        binNum: "Master Bin",
        binLocation: "Master Bin",
        phyQuantity: spareItem.phyQuantity || 0,
      },
    ];
  }

  return activeBins;
};

const autoSelectBinsForQty = (activeBins, targetQty) => {
  if (!activeBins || activeBins.length === 0) return { selectedBins: [], totalQty: 0 };
  if (!targetQty || targetQty <= 0) {
    const bName = activeBins[0].binNum || activeBins[0].binLocation;
    return { selectedBins: [bName], totalQty: activeBins[0].phyQuantity || 0 };
  }

  const selectedBins = [];
  let currentSum = 0;
  for (const b of activeBins) {
    const bName = b.binNum || b.binLocation;
    selectedBins.push(bName);
    currentSum += (b.phyQuantity || 0);
    if (currentSum >= targetQty) break;
  }

  return { selectedBins, totalQty: currentSum };
};

const AddTransferSparesModal = ({
  open,
  close,
  refreshData,
  branchOptions,
  form,
  createAccess = false,
  initialValues = null,
}) => {
  const [vehicles, setVehicle] = useState([]);
  const [data, setData] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]); // Store all vehicles for the selected branch
  const [isSearching, setIsSearching] = useState(false);

  const {
    getFieldDecorator,
    getFieldValue,
    setFieldsValue,
    resetFields,
  } = form;

  useEffect(() => {
    if (open) {
      resetFields();
      if (initialValues && initialValues.fromBranchId) {
        setFieldsValue({
          fromBranch: initialValues.fromBranchId,
          toBranch: initialValues.toBranchId,
        });
        platformApi
          .post("/api/inventoryTransfers/spares", { branch: initialValues.fromBranchId })
          .then((result) => {
            let { data: resData } = result;
            if (resData.code === 200) {
              setVehicle(resData.data);
              setAllVehicles(resData.data);

              // Attach activeBins to initial rows once branch spares arrive
              setData((prevRows) =>
                prevRows.map((row) => {
                  if (!row.partNumber) return row;
                  const match = resData.data.find(
                    (v) => v.partNumber === row.partNumber || v.partNo === row.partNo
                  );
                  if (match) {
                    const activeBins = extractActiveBins(match);
                    const autoSelect = autoSelectBinsForQty(activeBins, row.quantity);

                    return {
                      ...row,
                      partNo: match.partNo,
                      activeBins: activeBins,
                      sourceBinLocations: autoSelect.selectedBins,
                      sourceBinLocation: autoSelect.selectedBins,
                      selectedBinPhyQty: autoSelect.totalQty,
                    };
                  }
                  return row;
                })
              );
            }
          });
        if (Array.isArray(initialValues.items) && initialValues.items.length > 0) {
          const rows = [];
          const fieldsObj = {};
          initialValues.items.forEach((item, idx) => {
            const rowKey = Date.now() + idx;
            rows.push({
              key: rowKey,
              partNo: item.partId,
              partNumber: item.partNumber,
              quantity: item.suggestedTransferQty,
              phyQuantity: item.availablePhyQty,
            });
            fieldsObj[`quantity_${rowKey}`] = item.suggestedTransferQty;
          });
          setData(rows);
          setTimeout(() => {
            setFieldsValue(fieldsObj);
          }, 50);
        } else {
          const rowKey = Date.now();
          setData([
            {
              key: rowKey,
              partNo: initialValues.partId,
              partNumber: initialValues.partNumber,
              quantity: initialValues.suggestedTransferQty,
              phyQuantity: initialValues.availablePhyQty,
            },
          ]);
          setTimeout(() => {
            setFieldsValue({ [`quantity_${rowKey}`]: initialValues.suggestedTransferQty });
          }, 50);
        }
      } else {
        setData([]);
        addVehicle();
        setAllVehicles([]);
      }
    }
    // eslint-disable-next-line
  }, [open, initialValues]);

  const debouncedSearch = debounce((searchValue, branchId) => {
    if (!searchValue.trim()) {
      // If search is empty, show all vehicles for the branch
      setVehicle(allVehicles);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    platformApi
      .post(`/api/inventoryTransfers/spares`, {
        branch: branchId,
        inputValue: searchValue,
      })
      .then((result) => {
        let { data } = result;
        if (data.code === 200) {
          setVehicle(data.data);
        }
        setIsSearching(false);
      })
      .catch(() => {
        setIsSearching(false);
      });
  }, 300);

  const handleFromBranchChange = (branchId) => {
    platformApi
      .post(`/api/inventoryTransfers/spares`, { branch: branchId })
      .then((result) => {
        let { data } = result;
        if (data.code === 200) {
          setVehicle(data.data);
          setAllVehicles(data.data); // Store all vehicles for this branch
        }
      });
    setFieldsValue({ toBranch: null });
    setData([]);
    addVehicle();
  };

  const addVehicle = () => {
    const newRow = {
      key: Date.now(),
      partNumber: null,
      quantity: null,
      phyQuantity: 0,
    };
    setData((prev) => [...prev, newRow]);
  };

  const handlePartChange = (partNumber, key) => {
    let selectedVehicle = allVehicles.find((v) => v.partNumber === partNumber);
    if (!selectedVehicle) {
      selectedVehicle = vehicles.find((v) => v.partNumber === partNumber);
    }

    if (!selectedVehicle) {
      message.error(`Part ${partNumber} not found in inventory.`);
      setData((prev) => prev.filter((row) => row.key !== key));
      return;
    }

    const totalAllocatedQuantity = data
      .filter((row) => row.partNumber === partNumber && row.key !== key)
      .reduce((sum, row) => sum + (row.quantity || 0), 0);

    const remainingQuantity =
      (selectedVehicle?.phyQuantity || 0) - totalAllocatedQuantity;

    if (remainingQuantity <= 0) {
      message.warning(
        `Part ${partNumber} has no remaining stock. Row removed.`
      );
      setData((prev) => prev.filter((row) => row.key !== key));
      return;
    }

    const activeBins = extractActiveBins(selectedVehicle);
    const autoSelect = autoSelectBinsForQty(activeBins, null);

    setData((prev) => {
      const updated = prev.map((row) => {
        if (row.key === key) {
          return {
            ...row,
            partNo: selectedVehicle.partNo,
            partNumber: partNumber,
            phyQuantity: remainingQuantity,
            activeBins: activeBins,
            sourceBinLocations: autoSelect.selectedBins,
            sourceBinLocation: autoSelect.selectedBins,
            selectedBinPhyQty: autoSelect.totalQty,
            quantity: null,
          };
        }
        return row;
      });

      const hasEmptyRow = updated.some((row) => !row.partNumber);
      if (!hasEmptyRow) {
        updated.push({
          key: Date.now(),
          partNumber: null,
          quantity: null,
          phyQuantity: 0,
        });
      }

      return updated;
    });
  };

  const handleBinChange = (selectedBinsArray, key, customBins) => {
    setData((prev) =>
      prev.map((row) => {
        if (row.key === key) {
          const binList = customBins || row.activeBins || [];
          const totalBinQty = (selectedBinsArray || []).reduce((sum, bName) => {
            const found = binList.find(
              (b) => (b.binNum || b.binLocation) === bName
            );
            return sum + (found ? found.phyQuantity || 0 : 0);
          }, 0);

          return {
            ...row,
            activeBins: binList,
            sourceBinLocations: selectedBinsArray,
            sourceBinLocation: selectedBinsArray,
            selectedBinPhyQty: totalBinQty,
          };
        }
        return row;
      })
    );
  };

  const handleQuantityChange = (value, key) => {
    let newQuantity = parseInt(value, 10);
    if (isNaN(newQuantity)) newQuantity = null;

    setData((prev) =>
      prev.map((row) => {
        if (row.key === key) {
          const activeBins = row.activeBins || [];
          // Preserve existing user-selected bin order if present
          let currentSelectedBins = row.sourceBinLocations;
          if (!currentSelectedBins || currentSelectedBins.length === 0) {
            if (activeBins.length > 0 && newQuantity > 0) {
              const autoSelect = autoSelectBinsForQty(activeBins, newQuantity);
              currentSelectedBins = autoSelect.selectedBins;
            }
          }

          const totalQty = (currentSelectedBins || []).reduce((sum, bName) => {
            const found = activeBins.find(
              (b) => (b.binNum || b.binLocation) === bName
            );
            return sum + (found ? found.phyQuantity || 0 : 0);
          }, 0);

          return {
            ...row,
            quantity: newQuantity,
            sourceBinLocations: currentSelectedBins,
            sourceBinLocation: currentSelectedBins,
            selectedBinPhyQty: totalQty,
          };
        }
        return row;
      })
    );

    setFieldsValue({ [`quantity_${key}`]: newQuantity });
    form.validateFields([`quantity_${key}`], { force: true });
  };

  const handleDeleteRow = (key) => {
    if (!key) return;

    setData((prev) => {
      const filtered = prev.filter((row) => row.key !== key);
      if (filtered.length === 0) {
        return [
          {
            key: Date.now(),
            partNumber: null,
            quantity: null,
            phyQuantity: 0,
          },
        ];
      }
      return filtered;
    });
  };

  const handleSubmit = () => {
    if (!createAccess) {
      message.error("You do not have permission to submit spares transfers.");
      return;
    }
    const fromBranch = getFieldValue("fromBranch");
    const toBranch = getFieldValue("toBranch");

    if (!fromBranch || !toBranch) {
      message.error("Please select both From and To branches!");
      return;
    }

    const validData = data.filter(
      (row) =>
        row.partNumber &&
        ((Array.isArray(row.sourceBinLocations) && row.sourceBinLocations.length > 0) || row.sourceBinLocation) &&
        row.quantity !== null &&
        row.quantity > 0
    );
    if (validData.length === 0) {
      message.error("Please add at least one valid part with quantity and source bin location!");
      return;
    }

    const partQuantities = {};
    for (const row of validData) {
      if (!partQuantities[row.partNumber]) {
        partQuantities[row.partNumber] = 0;
      }
      partQuantities[row.partNumber] += row.quantity;
    }

    for (const partNumber in partQuantities) {
      const selectedVehicle = allVehicles.find((v) => v.partNumber === partNumber);
      if (!selectedVehicle) {
        message.error(`Part ${partNumber} not found in inventory.`);
        return;
      }

      if ((selectedVehicle?.phyQuantity || 0) < partQuantities[partNumber]) {
        message.error(
          `Total quantity for part ${partNumber} exceeds available stock (${selectedVehicle?.phyQuantity || 0})`
        );
        return;
      }
    }

    const payload = {
      spares: validData.map((row) => ({
        partNo: row.partNo,
        quantity: row.quantity,
        sourceBinLocation: row.sourceBinLocations || row.sourceBinLocation,
      })),
      from_branch: fromBranch,
      to_branch: toBranch,
    };

    platformApi
      .put(`/api/inventoryTransfers/spares`, payload)
      .then((result) => {
        if (result.status === 200) {
          message.success(result.data.data.message);
          window.dispatchEvent(new Event("pendingTransfersUpdated"));
          setData([]);
          addVehicle();
          resetFields(["fromBranch", "toBranch"]);
          close();
          if (refreshData) refreshData();
        } else {
          message.error(`Transfer failed: ${result.data.message}`);
        }
      })
      .catch((error) => {
        message.error("An error occurred during transfer.");
        console.error(error);
      });
  };

  const columns = [
    {
      title: "Part Number",
      key: "partNumber",
      width: 170,
      render: (text, record) => (
        <Select
          className="ChallanTable-Manufacturers"
          showSearch
          value={record.partNumber}
          filterOption={false}
          notFoundContent={isSearching ? "Searching..." : null}
          disabled={!getFieldValue("fromBranch")}
          dropdownStyle={{ overflowY: "auto", overflowX: "hidden" }}
          onChange={(value) => handlePartChange(value, record.key)}
          onSearch={(value) => {
            debouncedSearch(value, getFieldValue("fromBranch"));
          }}
          onFocus={() => {
            if (!getFieldValue("fromBranch")) return;
            setVehicle(allVehicles);
          }}
          placeholder="Part Number"
        >
          {vehicles?.map((item) => (
            <Option key={item.partNumber} value={item.partNumber}>
              {item.partNumber}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Source Bin Location",
      key: "sourceBinLocation",
      width: 230,
      render: (text, record) => {
        const matchedSpare =
          allVehicles.find(
            (v) => v.partNumber === record.partNumber || v.partNo === record.partNo
          ) ||
          vehicles.find(
            (v) => v.partNumber === record.partNumber || v.partNo === record.partNo
          );

        const activeBins =
          record.activeBins && record.activeBins.length > 0
            ? record.activeBins
            : extractActiveBins(matchedSpare);

        let currentVal = [];
        if (Array.isArray(record.sourceBinLocations)) {
          currentVal = record.sourceBinLocations;
        } else if (record.sourceBinLocation) {
          currentVal = typeof record.sourceBinLocation === 'string'
            ? record.sourceBinLocation.split(',').map(s => s.trim()).filter(Boolean)
            : [record.sourceBinLocation];
        } else if (activeBins.length > 0) {
          const autoSelect = autoSelectBinsForQty(activeBins, record.quantity);
          currentVal = autoSelect.selectedBins;
        }

        return (
          <Select
            mode="multiple"
            placeholder="Select Bin Location(s)"
            value={currentVal}
            disabled={!record.partNumber}
            className="multi-bin-select"
            dropdownStyle={{ overflowY: "auto", overflowX: "hidden" }}
            onChange={(valueArray) => handleBinChange(valueArray, record.key, activeBins)}
          >
            {activeBins.map((b) => {
              const bName = b.binNum || b.binLocation;
              return (
                <Option key={bName} value={bName}>
                  {bName}
                </Option>
              );
            })}
          </Select>
        );
      },
    },
    {
      title: "Quantity",
      width: 105,
      render: (text, record) => {
        const matchedSpare =
          allVehicles.find(
            (v) => v.partNumber === record.partNumber || v.partNo === record.partNo
          ) ||
          vehicles.find(
            (v) => v.partNumber === record.partNumber || v.partNo === record.partNo
          );
        const activeBins =
          record.activeBins && record.activeBins.length > 0
            ? record.activeBins
            : extractActiveBins(matchedSpare);

        let currentSelectedBins = [];
        if (Array.isArray(record.sourceBinLocations)) {
          currentSelectedBins = record.sourceBinLocations;
        } else if (record.sourceBinLocation) {
          currentSelectedBins = typeof record.sourceBinLocation === 'string'
            ? record.sourceBinLocation.split(',').map(s => s.trim()).filter(Boolean)
            : [record.sourceBinLocation];
        } else if (activeBins.length > 0) {
          currentSelectedBins = autoSelectBinsForQty(activeBins, record.quantity).selectedBins;
        }

        const maxAvailable = currentSelectedBins.reduce((sum, bName) => {
          const found = activeBins.find((b) => (b.binNum || b.binLocation) === bName);
          return sum + (found ? found.phyQuantity || 0 : 0);
        }, 0);

        const isError = record.quantity !== null && record.quantity > maxAvailable;
        return (
          <Form.Item
            validateStatus={isError ? "error" : ""}
            help={isError ? "Exceeds bin stock" : ""}
            style={{ marginBottom: 0 }}
          >
            {form.getFieldDecorator(`quantity_${record.key}`, {
              initialValue: record.quantity,
              rules: [
                { required: true, message: "Quantity is required" },
                {
                  validator: (_, value, callback) => {
                    if (value && value > maxAvailable) {
                      callback("Exceeds bin stock");
                    } else {
                      callback();
                    }
                  },
                },
              ],
            })(
              <Input
                type="number"
                min={1}
                onChange={(e) => handleQuantityChange(e.target.value, record.key)}
                disabled={!record.partNumber}
                placeholder="Enter Quantity"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addVehicle();
                  }
                }}
              />
            )}
          </Form.Item>
        );
      },
    },
    {
      title: "Bin Available",
      width: 95,
      render: (text, record) => {
        const matchedSpare =
          allVehicles.find(
            (v) => v.partNumber === record.partNumber || v.partNo === record.partNo
          ) ||
          vehicles.find(
            (v) => v.partNumber === record.partNumber || v.partNo === record.partNo
          );
        const activeBins =
          record.activeBins && record.activeBins.length > 0
            ? record.activeBins
            : extractActiveBins(matchedSpare);

        let currentSelectedBins = [];
        if (Array.isArray(record.sourceBinLocations)) {
          currentSelectedBins = record.sourceBinLocations;
        } else if (record.sourceBinLocation) {
          currentSelectedBins = typeof record.sourceBinLocation === 'string'
            ? record.sourceBinLocation.split(',').map(s => s.trim()).filter(Boolean)
            : [record.sourceBinLocation];
        } else if (activeBins.length > 0) {
          currentSelectedBins = autoSelectBinsForQty(activeBins, record.quantity).selectedBins;
        }

        const maxAvailable = currentSelectedBins.reduce((sum, bName) => {
          const found = activeBins.find((b) => (b.binNum || b.binLocation) === bName);
          return sum + (found ? found.phyQuantity || 0 : 0);
        }, 0);

        return (
          <Form.Item style={{ marginBottom: 0 }}>
            <Input
              disabled
              placeholder="Available"
              value={currentSelectedBins.length > 0 ? maxAvailable : "-"}
            />
          </Form.Item>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 85,
      render: (text, record) => (
        <Button
          type="danger"
          icon="delete"
          onClick={() => handleDeleteRow(record.key)}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title="Add Spare Transfer"
      visible={open}
      onCancel={close}
      footer={null}
      width={980}
      destroyOnClose
    >
      <style>{`
        .multi-bin-select .ant-select-selection--multiple {
          max-height: 52px !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
        }
        .multi-bin-select .ant-select-selection--multiple::-webkit-scrollbar {
          width: 8px;
        }
        .multi-bin-select .ant-select-selection--multiple::-webkit-scrollbar-track {
          background: #e8e8e8;
          border-radius: 4px;
        }
        .multi-bin-select .ant-select-selection--multiple::-webkit-scrollbar-thumb {
          background: #999999;
          border-radius: 4px;
        }
        .multi-bin-select .ant-select-selection--multiple::-webkit-scrollbar-thumb:hover {
          background: #555555;
        }
        .multi-bin-select .ant-select-selection__rendered {
          display: flex !important;
          flex-wrap: wrap !important;
          align-items: center !important;
          gap: 2px !important;
        }
        .multi-bin-select .ant-select-selection__rendered > ul {
          display: flex !important;
          flex-wrap: wrap !important;
          align-items: center !important;
          gap: 2px !important;
        }
        .multi-bin-select .ant-select-selection__choice {
          margin-top: 2px !important;
          margin-bottom: 2px !important;
        }
      `}</style>
      <Form>
        <Row type="flex" style={{ gap: "30px" }}>
          <Col span={8}>
            <span>From Branch</span>
            <Item>
              {getFieldDecorator("fromBranch")(
                <Select
                  placeholder="From Branch"
                  onChange={handleFromBranchChange}
                >
                  {branchOptions.map((branch) => (
                    <Option key={branch.id} value={branch.id}>
                      {branch.name}
                    </Option>
                  ))}
                </Select>
              )}
            </Item>
          </Col>
          <Col span={8}>
            <span>To Branch</span>
            <Item>
              {getFieldDecorator("toBranch", {
                rules: [{ required: true }],
              })(
                <Select
                  placeholder="To Branch"
                  disabled={!getFieldValue("fromBranch")}
                >
                  {branchOptions
                    .filter(
                      (branch) => branch.id !== getFieldValue("fromBranch")
                    )
                    .map((branch) => (
                      <Option key={branch.id} value={branch.id}>
                        {branch.name}
                      </Option>
                    ))}
                </Select>
              )}
            </Item>
          </Col>
        </Row>
        <div style={{ margin: "16px 0 8px 0", fontWeight: 500 }}>
          Spare Parts Details
        </div>
        <Table
          size="small"
          pagination={false}
          scroll={{ y: 300 }}
          className="Challantable"
          columns={columns}
          rowKey="key"
          dataSource={data}
        />
        <div style={{ textAlign: "right" }}>
          <Button
            type="primary"
            onClick={handleSubmit}
            style={{ marginTop: 16 }}
            disabled={
              !createAccess ||
              !getFieldValue("fromBranch") ||
              !getFieldValue("toBranch") ||
              data
                .filter((row, index) => {
                  const isLast = index === data.length - 1;
                  return !(isLast && !row.partNumber);
                })
                .length === 0 ||
              data
                .filter((row, index) => {
                  const isLast = index === data.length - 1;
                  return !(isLast && !row.partNumber);
                })
                .some((row) => {
                  const matchedSpare =
                    allVehicles.find(
                      (v) => v.partNumber === row.partNumber || v.partNo === row.partNo
                    ) ||
                    vehicles.find(
                      (v) => v.partNumber === row.partNumber || v.partNo === row.partNo
                    );
                  const activeBins =
                    row.activeBins && row.activeBins.length > 0
                      ? row.activeBins
                      : extractActiveBins(matchedSpare);

                  let currentSelectedBins = [];
                  if (Array.isArray(row.sourceBinLocations)) {
                    currentSelectedBins = row.sourceBinLocations;
                  } else if (row.sourceBinLocation) {
                    currentSelectedBins = typeof row.sourceBinLocation === 'string'
                      ? row.sourceBinLocation.split(',').map(s => s.trim()).filter(Boolean)
                      : [row.sourceBinLocation];
                  } else if (activeBins.length > 0) {
                    currentSelectedBins = autoSelectBinsForQty(activeBins, row.quantity).selectedBins;
                  }

                  const maxAvail = currentSelectedBins.reduce((sum, bName) => {
                    const found = activeBins.find((b) => (b.binNum || b.binLocation) === bName);
                    return sum + (found ? found.phyQuantity || 0 : 0);
                  }, 0);

                  return (
                    !row.partNumber ||
                    currentSelectedBins.length === 0 ||
                    !row.quantity ||
                    row.quantity <= 0 ||
                    row.quantity > maxAvail ||
                    form.getFieldError(`quantity_${row.key}`)
                  );
                })
            }
          >
            Transfer Spares
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default Form.create()(AddTransferSparesModal);